import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AdminShell } from '../components/admin/AdminShell';
import { adminApi } from '../services/adminApi';
import { ConsentRecord, PaginatedResponse, EvidenceData, TemplateConsentData, LocationInfo, DocumentSnapshotData } from '../types/admin';
import { useRegion } from '../contexts/RegionContext';
import { formatDateTime } from '../utils/dateFormat';
import {
  PageHeader,
  FilterGroup,
  SelectInput,
  Table,
  Th,
  Td,
  Tr,
  StatusBadge,
  IconButton,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  CloseButton,
  AlertMessage,
  PaginationContainer,
  PaginationInfo,
  PaginationButtons,
  PaginationButton,
} from '../styles/adminShared';
import { theme, neutral } from '../design-system/theme';

// Custom styled components for this page (complex layouts)
const Modal = styled(ModalOverlay)`
  overflow-y: auto;
  padding: 40px 20px;
`;

const ModalContentLarge = styled(ModalContent)`
  max-width: 1000px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0;
  color: ${theme.textPrimary};
  font-weight: 600;
  font-size: 14px;
`;

const ActionButton = styled(IconButton)`
  padding: 8px 16px;
  font-size: 12px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  color: ${theme.textPrimary};
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid ${theme.primary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
`;

const InfoCard = styled.div`
  background-color: ${neutral[1]};
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid ${theme.primary};
`;

const InfoLabel = styled.div`
  font-size: 12px;
  color: ${theme.textSecondary};
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  color: ${theme.textPrimary};
  word-break: break-word;
  font-family: ${props => props.className?.includes('mono') ? 'monospace' : 'inherit'};
`;

const EvidenceCard = styled.div`
  background-color: ${neutral[1]};
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 12px;
`;

const EvidenceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const EvidenceType = styled.span`
  background-color: ${theme.primary};
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const VerifiedBadge = styled.span<{ $verified: boolean }>`
  background-color: ${props => props.$verified ? '#27ae60' : '#e74c3c'};
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
`;

const SignatureImage = styled.img`
  max-width: 100%;
  max-height: 150px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
`;

const TemplateCard = styled.div`
  background-color: ${neutral[1]};
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid ${neutral[3]};
`;

const TemplateHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const TemplateName = styled.h4`
  margin: 0;
  color: ${theme.textPrimary};
  font-size: 16px;
`;

const TemplateInfo = styled.div`
  font-size: 13px;
  color: ${theme.textSecondary};
  margin-top: 4px;
`;

const DocumentCard = styled.div`
  background-color: white;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 8px;
  border: 1px solid ${neutral[3]};
`;

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  border-bottom: 1px solid ${neutral[3]};
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 12px 20px;
  border: none;
  background-color: ${props => props.$active ? theme.primary : 'transparent'};
  color: ${props => props.$active ? 'white' : theme.textSecondary};
  font-weight: 600;
  cursor: pointer;
  border-radius: 6px 6px 0 0;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.$active ? 'inherit' : neutral[2]};
  }
`;

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
};

const getEvidenceTypeIcon = (type: string): string => {
  switch (type?.toLowerCase()) {
    case 'signature': return '✍️';
    case 'phone_otp': return '📱';
    case 'email_otp': return '📧';
    case 'video': return '🎥';
    case 'audio': return '🎤';
    default: return '📋';
  }
};

const getEvidenceTypeLabel = (type: string): string => {
  switch (type?.toLowerCase()) {
    case 'signature': return 'Digital Signature';
    case 'phone_otp': return 'Phone OTP';
    case 'email_otp': return 'Email OTP';
    case 'video': return 'Video Recording';
    case 'audio': return 'Audio Recording';
    default: return type || 'Unknown';
  }
};

export const ConsentRecords: React.FC = () => {
  const { selectedRegion } = useRegion();
  const [records, setRecords] = useState<PaginatedResponse<ConsentRecord> | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<ConsentRecord | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'evidence' | 'templates' | 'snapshot'>('overview');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, selectedRegion]);

  const loadRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.listConsentRecords(
        currentPage,
        20,
        undefined,
        statusFilter || undefined,
        selectedRegion.code
      );
      
      setRecords(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load consent artifacts');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (consentId: string) => {
    try {
      const record = await adminApi.getConsentRecord(consentId);
      setSelectedRecord(record);
      setActiveTab('overview');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load consent artifact details');
    }
  };

  const renderLocation = (location?: LocationInfo) => {
    if (!location) return 'N/A';
    
    const parts = [];
    if (location.city) parts.push(location.city);
    if (location.region) parts.push(location.region);
    if (location.country) parts.push(location.country);
    
    if (parts.length === 0 && location.latitude && location.longitude) {
      return `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`;
    }
    
    return parts.join(', ') || 'N/A';
  };

  const renderEvidence = (evidence: EvidenceData) => {
    return (
      <EvidenceCard key={evidence.evidenceId || evidence.type}>
        <EvidenceHeader>
          <EvidenceType>
            {getEvidenceTypeIcon(evidence.type)} {getEvidenceTypeLabel(evidence.type)}
          </EvidenceType>
          <VerifiedBadge $verified={evidence.verified || false}>
            {evidence.verified ? '✓ Verified' : '✗ Not Verified'}
          </VerifiedBadge>
        </EvidenceHeader>
        
        {evidence.type === 'signature' && evidence.data && (
          <div>
            <SignatureImage src={evidence.data} alt="Signature" />
          </div>
        )}
        
        {(evidence.type === 'phone_otp' || evidence.type === 'email_otp') && (
          <InfoGrid>
            <InfoCard>
              <InfoLabel>OTP Verified</InfoLabel>
              <InfoValue>{evidence.verified ? 'Yes' : 'No'}</InfoValue>
            </InfoCard>
            {evidence.capturedAt && (
              <InfoCard>
                <InfoLabel>Captured At</InfoLabel>
                <InfoValue>{formatDateTime(evidence.capturedAt)}</InfoValue>
              </InfoCard>
            )}
          </InfoGrid>
        )}
        
        {(evidence.type === 'video' || evidence.type === 'audio') && evidence.data && (
          <div>
            <InfoCard>
              <InfoLabel>Recording URL</InfoLabel>
              <InfoValue style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                {evidence.data}
              </InfoValue>
            </InfoCard>
          </div>
        )}
      </EvidenceCard>
    );
  };

  const renderTemplateConsent = (tc: TemplateConsentData) => {
    return (
      <TemplateCard key={tc.templateId}>
        <TemplateHeader>
          <div>
            <TemplateName>{tc.templateName || tc.templateId}</TemplateName>
            {tc.projectName && <TemplateInfo>Project: {tc.projectName}</TemplateInfo>}
            {tc.dataCollectorName && <TemplateInfo>Data Collector: {tc.dataCollectorName}</TemplateInfo>}
          </div>
          <StatusBadge $status={tc.consentAccepted ? 'ACCEPTED' : 'REJECTED'}>
            {tc.consentAccepted ? 'Accepted' : 'Rejected'}
          </StatusBadge>
        </TemplateHeader>
        
        <InfoGrid>
          <InfoCard>
            <InfoLabel>Status</InfoLabel>
            <InfoValue>{tc.status || 'N/A'}</InfoValue>
          </InfoCard>
          {tc.submittedAt && (
            <InfoCard>
              <InfoLabel>Submitted At</InfoLabel>
              <InfoValue>{formatDateTime(tc.submittedAt)}</InfoValue>
            </InfoCard>
          )}
        </InfoGrid>
        
        {tc.signatureData && (
          <div style={{ marginTop: '12px' }}>
            <InfoLabel>Signature</InfoLabel>
            <SignatureImage src={tc.signatureData} alt="Template Signature" />
          </div>
        )}
        
        {tc.evidences && tc.evidences.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <InfoLabel style={{ marginBottom: '8px' }}>Evidence ({tc.evidences.length})</InfoLabel>
            {tc.evidences.map(renderEvidence)}
          </div>
        )}
      </TemplateCard>
    );
  };

  const renderDocument = (doc: DocumentSnapshotData) => {
    const getDocTypeLabel = (type?: string) => {
      switch (type) {
        case 'privacy_policy': return '🔒 Privacy Policy';
        case 'consent': return '📝 Consent Text';
        case 'terms_of_service': return '📋 Terms of Service';
        default: return type || 'Document';
      }
    };

    return (
      <DocumentCard key={doc.type}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <strong>{getDocTypeLabel(doc.type)}</strong>
          {doc.wasAccepted !== undefined && (
            <StatusBadge $status={doc.wasAccepted ? 'ACCEPTED' : 'REJECTED'}>
              {doc.wasAccepted ? 'Accepted' : 'Not Accepted'}
            </StatusBadge>
          )}
        </div>
        <div style={{ fontSize: '12px', color: theme.textSecondary }}>
          {doc.dataType && <span>Type: {doc.dataType}</span>}
          {doc.contentHash && <span style={{ marginLeft: '12px' }}>Hash: {doc.contentHash.substring(0, 16)}...</span>}
        </div>
      </DocumentCard>
    );
  };

  if (loading && !records) {
    return (
      <AdminShell title="Consent Artifacts">
        <div>Loading...</div>
      </AdminShell>
    );
  }

  const totalRecords = records?.pagination?.totalItems || records?.totalElements || 0;
  const totalPages = records?.pagination?.totalPages || records?.totalPages || 1;

  return (
    <AdminShell title="Consent Artifacts">
      <PageHeader>
        <FilterGroup>
          <Label>Filter by Status:</Label>
          <SelectInput
            value={statusFilter}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Status</option>
            <option value="COMPLETED">Completed</option>
            <option value="PENDING">Pending</option>
            <option value="WITHDRAWN">Withdrawn</option>
            <option value="EXPIRED">Expired</option>
          </SelectInput>
          <div style={{ color: theme.textSecondary, marginLeft: '16px' }}>
            Total: {totalRecords} artifact(s)
          </div>
        </FilterGroup>
      </PageHeader>

      {error && !selectedRecord && <AlertMessage $variant="error">{error}</AlertMessage>}

      <Table>
        <thead>
          <tr>
            <Th>Artifact ID</Th>
            <Th>Data Principal</Th>
            <Th>Status</Th>
            <Th>Erasure</Th>
            <Th>Device</Th>
            <Th>Submitted At</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {(records?.data || records?.content || []).map((record) => (
            <Tr key={record.consentId}>
              <Td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {record.consentId.substring(0, 12)}...
              </Td>
              <Td>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontWeight: 500, fontSize: '13px' }}>
                    {record.dataOwner?.maskedName || 'Unknown'}
                  </span>
                  {record.dataOwner?.maskedEmail && (
                    <span style={{ fontSize: '11px', color: theme.textSecondary }}>
                      {record.dataOwner.maskedEmail}
                    </span>
                  )}
                  {record.dataOwner?.internalId && (
                    <span style={{ fontSize: '10px', color: theme.textSecondary, fontFamily: 'monospace' }}>
                      ID: {record.dataOwner.internalId}
                    </span>
                  )}
                </div>
              </Td>
              <Td>
                <StatusBadge $status={record.status || (record.consentAccepted ? 'ACCEPTED' : 'REJECTED')}>
                  {record.status || (record.consentAccepted ? 'ACCEPTED' : 'REJECTED')}
                </StatusBadge>
              </Td>
              <Td>
                {(record.status === 'WITHDRAWN' || record.status === 'EXPIRED') ? (
                  <StatusBadge $status={record.dataErasureConfirmedAt ? 'COMPLETED' : (record.dataErasureRequired ? 'PENDING' : 'N/A')}>
                    {record.dataErasureConfirmedAt ? '✓ Confirmed' : (record.dataErasureRequired ? '⏳ Pending' : '—')}
                  </StatusBadge>
                ) : (
                  <span style={{ color: theme.textSecondary }}>—</span>
                )}
              </Td>
              <Td>{record.deviceInfo || 'N/A'}</Td>
              <Td>{record.submittedAt ? formatDateTime(record.submittedAt) : 'N/A'}</Td>
              <Td>
                <ActionButton $variant="view" onClick={() => handleViewDetails(record.consentId)}>
                  👁️ View
                </ActionButton>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>

      {totalPages > 1 && (
        <PaginationContainer>
          <PaginationInfo>
            Page {currentPage} of {totalPages}
          </PaginationInfo>
          <PaginationButtons>
            <PaginationButton
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </PaginationButton>
            <PaginationButton
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              Next
            </PaginationButton>
          </PaginationButtons>
        </PaginationContainer>
      )}

      {/* Detail Modal */}
      <Modal $show={!!selectedRecord}>
        {selectedRecord && (
          <ModalContentLarge>
            <ModalHeader>
              <ModalTitle>📋 Consent Artifact Details</ModalTitle>
              <CloseButton onClick={() => setSelectedRecord(null)}>✕</CloseButton>
            </ModalHeader>

            <Tabs>
              <Tab $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
                Overview
              </Tab>
              <Tab $active={activeTab === 'evidence'} onClick={() => setActiveTab('evidence')}>
                Evidence ({selectedRecord.evidences?.length || 0})
              </Tab>
              <Tab $active={activeTab === 'templates'} onClick={() => setActiveTab('templates')}>
                Templates ({selectedRecord.templateConsents?.length || 0})
              </Tab>
              <Tab $active={activeTab === 'snapshot'} onClick={() => setActiveTab('snapshot')}>
                Snapshot
              </Tab>
            </Tabs>

            <ModalBody>
              {activeTab === 'overview' && (
                <>
                  <Section>
                    <SectionTitle>👤 Data Principal</SectionTitle>
                    <InfoGrid>
                      <InfoCard>
                        <InfoLabel>Name</InfoLabel>
                        <InfoValue>{selectedRecord.dataOwner?.maskedName || 'Unknown'}</InfoValue>
                      </InfoCard>
                      {selectedRecord.dataOwner?.maskedEmail && (
                        <InfoCard>
                          <InfoLabel>Email</InfoLabel>
                          <InfoValue>{selectedRecord.dataOwner.maskedEmail}</InfoValue>
                        </InfoCard>
                      )}
                      {selectedRecord.dataOwner?.maskedPhone && (
                        <InfoCard>
                          <InfoLabel>Phone</InfoLabel>
                          <InfoValue>{selectedRecord.dataOwner.maskedPhone}</InfoValue>
                        </InfoCard>
                      )}
                      {selectedRecord.dataOwner?.internalId && (
                        <InfoCard>
                          <InfoLabel>Internal ID</InfoLabel>
                          <InfoValue style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                            {selectedRecord.dataOwner.internalId}
                          </InfoValue>
                        </InfoCard>
                      )}
                      {selectedRecord.dataOwner?.regionCode && (
                        <InfoCard>
                          <InfoLabel>Region</InfoLabel>
                          <InfoValue>{selectedRecord.dataOwner.regionCode}</InfoValue>
                        </InfoCard>
                      )}
                    </InfoGrid>
                  </Section>

                  <Section>
                    <SectionTitle>📌 Artifact Information</SectionTitle>
                    <InfoGrid>
                      <InfoCard>
                        <InfoLabel>Artifact ID</InfoLabel>
                        <InfoValue style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                          {selectedRecord.consentId}
                        </InfoValue>
                      </InfoCard>
                      <InfoCard>
                        <InfoLabel>Status</InfoLabel>
                        <InfoValue>
                          <StatusBadge $status={selectedRecord.status || (selectedRecord.consentAccepted ? 'ACCEPTED' : 'REJECTED')}>
                            {selectedRecord.status || (selectedRecord.consentAccepted ? 'ACCEPTED' : 'REJECTED')}
                          </StatusBadge>
                        </InfoValue>
                      </InfoCard>
                      <InfoCard>
                        <InfoLabel>Completion Time</InfoLabel>
                        <InfoValue>
                          {selectedRecord.completionTimeSeconds 
                            ? formatDuration(selectedRecord.completionTimeSeconds)
                            : 'N/A'}
                        </InfoValue>
                      </InfoCard>
                      {selectedRecord.regionCode && (
                        <InfoCard>
                          <InfoLabel>Region Code</InfoLabel>
                          <InfoValue>{selectedRecord.regionCode}</InfoValue>
                        </InfoCard>
                      )}
                    </InfoGrid>
                  </Section>

                  <Section>
                    <SectionTitle>🕐 Timestamps</SectionTitle>
                    <InfoGrid>
                      <InfoCard>
                        <InfoLabel>Submitted At</InfoLabel>
                        <InfoValue>
                          {selectedRecord.submittedAt 
                            ? formatDateTime(selectedRecord.submittedAt)
                            : 'N/A'}
                        </InfoValue>
                      </InfoCard>
                      <InfoCard>
                        <InfoLabel>Created At</InfoLabel>
                        <InfoValue>
                          {selectedRecord.createdAt 
                            ? formatDateTime(selectedRecord.createdAt)
                            : 'N/A'}
                        </InfoValue>
                      </InfoCard>
                      {selectedRecord.expiresAt && (
                        <InfoCard>
                          <InfoLabel>Expires At</InfoLabel>
                          <InfoValue>{new Date(selectedRecord.expiresAt).toLocaleString()}</InfoValue>
                        </InfoCard>
                      )}
                      {selectedRecord.withdrawnAt && (
                        <InfoCard>
                          <InfoLabel>Withdrawn At</InfoLabel>
                          <InfoValue>{new Date(selectedRecord.withdrawnAt).toLocaleString()}</InfoValue>
                        </InfoCard>
                      )}
                    </InfoGrid>
                  </Section>

                  <Section>
                    <SectionTitle>🖥️ Device & Browser</SectionTitle>
                    <InfoGrid>
                      <InfoCard>
                        <InfoLabel>Device Type</InfoLabel>
                        <InfoValue>{selectedRecord.deviceInfo || 'N/A'}</InfoValue>
                      </InfoCard>
                      <InfoCard>
                        <InfoLabel>IP Address</InfoLabel>
                        <InfoValue style={{ fontFamily: 'monospace' }}>
                          {selectedRecord.ipAddress || 'N/A'}
                        </InfoValue>
                      </InfoCard>
                      <InfoCard style={{ gridColumn: 'span 2' }}>
                        <InfoLabel>User Agent</InfoLabel>
                        <InfoValue style={{ fontSize: '12px' }}>
                          {selectedRecord.userAgent || 'N/A'}
                        </InfoValue>
                      </InfoCard>
                    </InfoGrid>
                  </Section>

                  <Section>
                    <SectionTitle>📍 Location</SectionTitle>
                    <InfoGrid>
                      <InfoCard>
                        <InfoLabel>Location</InfoLabel>
                        <InfoValue>{renderLocation(selectedRecord.location)}</InfoValue>
                      </InfoCard>
                      {selectedRecord.location?.source && (
                        <InfoCard>
                          <InfoLabel>Source</InfoLabel>
                          <InfoValue>
                            {selectedRecord.location.source === 'gps' ? '📍 GPS' : '🌐 IP Geolocation'}
                          </InfoValue>
                        </InfoCard>
                      )}
                      {selectedRecord.location?.accuracy && (
                        <InfoCard>
                          <InfoLabel>Accuracy</InfoLabel>
                          <InfoValue>
                            {selectedRecord.location.accuracy.toFixed(1)}m
                            {selectedRecord.location.accuracyAccepted !== undefined && (
                              <StatusBadge 
                                $status={selectedRecord.location.accuracyAccepted ? 'ACCEPTED' : 'REJECTED'}
                                style={{ marginLeft: '8px' }}
                              >
                                {selectedRecord.location.accuracyAccepted ? 'Accepted' : 'Low Accuracy'}
                              </StatusBadge>
                            )}
                          </InfoValue>
                        </InfoCard>
                      )}
                      {selectedRecord.location?.timezone && (
                        <InfoCard>
                          <InfoLabel>Timezone</InfoLabel>
                          <InfoValue>{selectedRecord.location.timezone}</InfoValue>
                        </InfoCard>
                      )}
                    </InfoGrid>
                  </Section>

                  {(selectedRecord.principalEmail || selectedRecord.principalPhone) && (
                    <Section>
                      <SectionTitle>👤 Principal Contact</SectionTitle>
                      <InfoGrid>
                        {selectedRecord.principalEmail && (
                          <InfoCard>
                            <InfoLabel>Email</InfoLabel>
                            <InfoValue>{selectedRecord.principalEmail}</InfoValue>
                          </InfoCard>
                        )}
                        {selectedRecord.principalPhone && (
                          <InfoCard>
                            <InfoLabel>Phone</InfoLabel>
                            <InfoValue>{selectedRecord.principalPhone}</InfoValue>
                          </InfoCard>
                        )}
                      </InfoGrid>
                    </Section>
                  )}

                  {/* Data Erasure Status (for withdrawn/expired consents) */}
                  {(selectedRecord.status === 'WITHDRAWN' || selectedRecord.status === 'EXPIRED') && (
                    <Section>
                      <SectionTitle>🗑️ Data Erasure Status</SectionTitle>
                      <InfoGrid>
                        <InfoCard>
                          <InfoLabel>Erasure Required</InfoLabel>
                          <InfoValue>
                            <StatusBadge $status={selectedRecord.dataErasureRequired ? 'PENDING' : 'N/A'}>
                              {selectedRecord.dataErasureRequired ? 'Yes' : 'No'}
                            </StatusBadge>
                          </InfoValue>
                        </InfoCard>
                        <InfoCard>
                          <InfoLabel>Erasure Status</InfoLabel>
                          <InfoValue>
                            <StatusBadge $status={selectedRecord.dataErasureConfirmedAt ? 'COMPLETED' : 'PENDING'}>
                              {selectedRecord.dataErasureConfirmedAt ? 'Confirmed' : 'Pending'}
                            </StatusBadge>
                          </InfoValue>
                        </InfoCard>
                        {selectedRecord.dataErasureConfirmedAt && (
                          <>
                            <InfoCard>
                              <InfoLabel>Confirmed At</InfoLabel>
                              <InfoValue>{formatDateTime(selectedRecord.dataErasureConfirmedAt)}</InfoValue>
                            </InfoCard>
                            <InfoCard>
                              <InfoLabel>Confirmed By</InfoLabel>
                              <InfoValue>{selectedRecord.dataErasureConfirmedBy || 'N/A'}</InfoValue>
                            </InfoCard>
                            {selectedRecord.dataErasureNotes && (
                              <InfoCard style={{ gridColumn: 'span 2' }}>
                                <InfoLabel>Erasure Notes</InfoLabel>
                                <InfoValue>{selectedRecord.dataErasureNotes}</InfoValue>
                              </InfoCard>
                            )}
                          </>
                        )}
                        {selectedRecord.withdrawalReason && (
                          <InfoCard style={{ gridColumn: 'span 2' }}>
                            <InfoLabel>Withdrawal Reason</InfoLabel>
                            <InfoValue>{selectedRecord.withdrawalReason}</InfoValue>
                          </InfoCard>
                        )}
                      </InfoGrid>
                    </Section>
                  )}
                </>
              )}

              {activeTab === 'evidence' && (
                <Section>
                  <SectionTitle>🔐 Evidence Collected</SectionTitle>
                  {selectedRecord.evidences && selectedRecord.evidences.length > 0 ? (
                    selectedRecord.evidences.map(renderEvidence)
                  ) : (
                    <div style={{ color: theme.textSecondary, textAlign: 'center', padding: '40px' }}>
                      No evidence data available
                    </div>
                  )}
                </Section>
              )}

              {activeTab === 'templates' && (
                <Section>
                  <SectionTitle>📄 Template Consents</SectionTitle>
                  {selectedRecord.templateConsents && selectedRecord.templateConsents.length > 0 ? (
                    selectedRecord.templateConsents.map(renderTemplateConsent)
                  ) : (
                    <div style={{ color: theme.textSecondary, textAlign: 'center', padding: '40px' }}>
                      No template consent data available
                    </div>
                  )}
                </Section>
              )}

              {activeTab === 'snapshot' && (
                <Section>
                  <SectionTitle>📸 Consent Snapshot</SectionTitle>
                  <p style={{ color: theme.textSecondary, marginBottom: '16px' }}>
                    This is an immutable record of what was shown to the user at the time of consent.
                  </p>
                  
                  {selectedRecord.snapshot?.templates && selectedRecord.snapshot.templates.length > 0 ? (
                    selectedRecord.snapshot.templates.map((ts, index) => (
                      <TemplateCard key={ts.templateId || index}>
                        <TemplateHeader>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {ts.projectLogo && (
                              <img 
                                src={ts.projectLogo} 
                                alt="" 
                                style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }}
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            )}
                            <div>
                              <TemplateName>{ts.projectName || 'Project'}</TemplateName>
                              <TemplateInfo>
                                {ts.dataCollectorName && `By: ${ts.dataCollectorName}`}
                                {ts.templateVersion && ` • Version: ${ts.templateVersion}`}
                              </TemplateInfo>
                            </div>
                          </div>
                        </TemplateHeader>
                        
                        {ts.dataCategories && ts.dataCategories.length > 0 && (
                          <div style={{ marginBottom: '16px' }}>
                            <InfoLabel>Data Categories</InfoLabel>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                              {ts.dataCategories.map((cat, i) => (
                                <span 
                                  key={i}
                                  style={{
                                    backgroundColor: theme.primary + '15',
                                    color: theme.primary,
                                    padding: '4px 12px',
                                    borderRadius: '12px',
                                    fontSize: '12px'
                                  }}
                                >
                                  {cat}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {ts.documents && ts.documents.length > 0 && (
                          <div>
                            <InfoLabel style={{ marginBottom: '8px' }}>Documents Shown</InfoLabel>
                            {ts.documents.map(renderDocument)}
                          </div>
                        )}
                      </TemplateCard>
                    ))
                  ) : (
                    <div style={{ color: theme.textSecondary, textAlign: 'center', padding: '40px' }}>
                      No snapshot data available
                    </div>
                  )}
                </Section>
              )}
            </ModalBody>

            <ModalFooter>
              <ActionButton onClick={() => setSelectedRecord(null)}>
                Close
              </ActionButton>
            </ModalFooter>
          </ModalContentLarge>
        )}
      </Modal>
    </AdminShell>
  );
};
